import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import {
  Alert,
  AlertActionLink,
  Breadcrumb,
  BreadcrumbItem,
  Bullseye,
  Button,
  Content,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  Flex,
  FlexItem,
  Icon,
  PageBreadcrumb,
  PageSection,
  PageSectionVariants,
  Spinner,
  Tab,
  TabTitleText,
  Tabs,
} from '@patternfly/react-core';
import { DomainIcon, ExclamationCircleIcon, SyncAltIcon, UserEditIcon } from '@patternfly/react-icons';
import Unauthenticated from '@app/Unauthenticated';
import { useDomainName } from '../../../hooks/useDomainName';
import { tradeIneligibilityReason } from './Trade/types';
import InTransferAlert from './InTransferAlert';
import DomainContactsTab from './DomainContactsTab';
import DomainDatesTab from './DomainDatesTab';
import DomainNameserversTab from './DomainNameserversTab';
import DomainDnsZoneTab from './DomainDnsZoneTab';

function DomainNameDetail() {
  const auth = useAuth();
  const { id: idParam } = useParams<{ id: string }>();
  const parsedId = Number(idParam);
  const idIsValid = Number.isInteger(parsedId) && parsedId > 0;

  const { domainName, loading, error, refetch } = useDomainName(idIsValid ? parsedId : 0);

  const [activeTabKey, setActiveTabKey] = useState<string | number>('dates');

  if (!auth.isAuthenticated) {
    return <Unauthenticated />;
  }

  const titleText = domainName?.name ?? 'Domain name';
  const breadcrumbLeaf = idIsValid ? (domainName?.name ?? (loading ? '…' : 'Not found')) : 'Not found';
  // The refresh action stays visible during a refetch (so the button can show
  // its own in-progress spinner); it only requires a loaded entity and no error.
  const showRefresh = idIsValid && !error && domainName !== null;
  // Entry to the internal-trade wizard — shown only for an eligible domain (.be, registrar-bound,
  // not in transfer); the wizard/backend re-check authoritatively.
  const showChangeOwner = showRefresh && domainName !== null && tradeIneligibilityReason(domainName) === null;

  const header = (
    <>
      <PageBreadcrumb hasBodyWrapper={false} hasShadowBottom={false}>
        <Breadcrumb ouiaId="BasicBreadcrumb">
          <BreadcrumbItem to="/datacenter">Datacenter</BreadcrumbItem>
          <BreadcrumbItem to="/datacenter/domains">Domain Names</BreadcrumbItem>
          <BreadcrumbItem isActive>{breadcrumbLeaf}</BreadcrumbItem>
        </Breadcrumb>
      </PageBreadcrumb>
      <PageSection hasBodyWrapper variant={PageSectionVariants.default} isWidthLimited hasShadowBottom={true}>
        <Flex
          justifyContent={{ default: 'justifyContentSpaceBetween' }}
          alignItems={{ default: 'alignItemsFlexStart' }}
        >
          <FlexItem>
            <Content component="h1">
              <Icon size="lg" style={{ marginRight: '8px' }}>
                <DomainIcon />
              </Icon>
              {titleText}
            </Content>
            <Content component="p">Domain name details</Content>
          </FlexItem>
          {showRefresh && (
            <FlexItem>
              <Flex spaceItems={{ default: 'spaceItemsSm' }}>
                {showChangeOwner && domainName !== null && (
                  <FlexItem>
                    <Button
                      variant="secondary"
                      icon={<UserEditIcon />}
                      component={(props) => (
                        <Link
                          {...props}
                          to={`/datacenter/domains/trade?domain=${encodeURIComponent(domainName.name)}`}
                        />
                      )}
                    >
                      Change owner
                    </Button>
                  </FlexItem>
                )}
                <FlexItem>
                  <Button
                    variant="secondary"
                    icon={<SyncAltIcon />}
                    isLoading={loading}
                    spinnerAriaValueText="Refreshing domain"
                    onClick={() => {
                      if (!loading) {
                        void refetch();
                      }
                    }}
                  >
                    Refresh
                  </Button>
                </FlexItem>
              </Flex>
            </FlexItem>
          )}
        </Flex>
      </PageSection>
    </>
  );

  const renderContent = () => {
    if (!idIsValid) {
      return (
        <EmptyState titleText="Domain not found" icon={ExclamationCircleIcon} variant="lg" headingLevel="h2">
          <EmptyStateBody>The requested domain name does not exist.</EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" component={(props) => <Link {...props} to="/datacenter/domains" />}>
                Back to domain names
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      );
    }

    // Full-page spinner only on the initial load; during a refetch the existing
    // content stays visible while the Refresh button shows its own spinner.
    if (loading && domainName === null) {
      return (
        <Bullseye>
          <Spinner size="xl" aria-label="Loading domain name" />
        </Bullseye>
      );
    }

    if (error) {
      return (
        <Alert
          variant="danger"
          isInline
          title="Failed to load domain name"
          actionLinks={<AlertActionLink onClick={() => void refetch()}>Retry</AlertActionLink>}
        >
          {error}
        </Alert>
      );
    }

    if (domainName === null) {
      return (
        <EmptyState titleText="Domain not found" icon={ExclamationCircleIcon} variant="lg" headingLevel="h2">
          <EmptyStateBody>The requested domain name does not exist.</EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" component={(props) => <Link {...props} to="/datacenter/domains" />}>
                Back to domain names
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      );
    }

    return (
      <>
        {domainName.in_transfer && (
          <InTransferAlert deadline={domainName.transfer_expires_at} className="pf-v6-u-mb-md" />
        )}
        <Tabs
          activeKey={activeTabKey}
          onSelect={(_event, eventKey) => setActiveTabKey(eventKey)}
          isBox
          mountOnEnter
          aria-label="Domain detail sections"
        >
          <Tab eventKey="contacts" title={<TabTitleText>Contacts</TabTitleText>}>
            <DomainContactsTab domainName={domainName} />
          </Tab>
          <Tab eventKey="dates" title={<TabTitleText>Dates</TabTitleText>}>
            <DomainDatesTab domainName={domainName} />
          </Tab>
          <Tab eventKey="nameservers" title={<TabTitleText>Nameservers</TabTitleText>}>
            <DomainNameserversTab domainName={domainName} onConfigured={refetch} />
          </Tab>
          <Tab eventKey="dns-zone" title={<TabTitleText>DNS Zone</TabTitleText>}>
            <DomainDnsZoneTab domainName={domainName} onRetry={refetch} />
          </Tab>
        </Tabs>
      </>
    );
  };

  return (
    <>
      {header}
      <PageSection hasBodyWrapper={false}>{renderContent()}</PageSection>
    </>
  );
}

export default DomainNameDetail;
